/**
 * Mainly to support modal behaviour: invokes a given callback function when user clicks
 * anywhere outside the given HTML element
 * @param node - The HTML element for which we are monitoring its surroundings for a click
 * @param callback - The function that should be invoked when the user clicks outside `node`
 * @param rightClick - Should right clicks also be counted as clicks?
 * */

import { goto } from "$app/navigation";
import type { Action } from "svelte/action";

// TODO: Can we just have one subscription to the click event on window?
export const notifyOnClickOutside = (node: Node, callback: () => unknown, rightClick = false) => {
	const handleClick = (event: Event) => {
		if (!node.contains(event.target as Node)) {
			callback();
		}
	};

	window.addEventListener("click", handleClick);
	if (rightClick) {
		// Without the setTimeout, the listeners get added too quickly so if this action is
		// part of a component that was created because of a contextmenu event, that same
		// contextmenu event could get picked up by this listener, whereas we only want to pick
		// up contextmenu events occurring AFTER that event.
		setTimeout(() => {
			window.addEventListener("contextmenu", handleClick);
		}, 0);
	}

	return {
		destroy() {
			window.removeEventListener("click", handleClick);
			if (rightClick) window.removeEventListener("contextmenu", handleClick);
		},
	}
};


/**
 * This Svelte Action allows yout to JIT set either the `href` with a path or alternatively
 * use the `onclick` attribute to perform a `goto` based on the `nolink` parameter.
 * 
 * *PURPOSE*: Our SEO consultant wants links that deviate from the canonical path to be
 * onclick navigation rather than legitimate links (i.e. `href` attribute set).
 * 
 * In our case we use the Google bot's environment to choose whether to set the `href`
 * attribute. When the bot is browsing there will be no user session and not location.
 * 
 * @param elm - The anchor element
 * @param path - The path to set as the `href` attribute. *No external links!*
 * @param nolink - If `true`, the `href` attribute will not be set and the `onclick` attribute
 * will be set to navigate to the given path using Svelte's `goto` function.
 */
export const attHref:Action<HTMLAnchorElement,{path:string,nolink?:boolean}> = (elm, {path, nolink}) => {
	let noScroll = false;
	for (const attr of elm.attributes) {
		if (attr.name?.startsWith('data-sveltekit-noscroll')) {
			noScroll = attr.value !== 'false';
		}
	}

	const rtn: ReturnType<typeof attHref> = {
		update({path, nolink}) {
			if (nolink) {
				elm.href = '#';
				elm.onclick = e => {
					e.preventDefault();
					goto(path, { noScroll });
				}
			} else {
				elm.href = path;
				elm.onclick = null;
			}
		}
	}

	if (elm.onclick) {
		console.warn('The attHref action can only be used with elements that don\'t have an onclick function assigned.');
		nolink = false;
	}

	rtn.update?.({path, nolink});

	return rtn
}

/**
 * Taken from https://svelte.dev/repl/f34b6159667247e6b6abb5142b276483?version=3.6.3
 * 
 * Long press action
 * @param node The element that needs a callback on long press
 * @param threshold The long press timeout
 * @returns The action object
 */
export function longpress(node:HTMLElement, threshold = 500) {
	const start = () => {
		let longpressed = false;
		
		const timeout = setTimeout(() => {
			node.dispatchEvent(new CustomEvent('longpress'));
			longpressed = true;
		}, threshold);
		
		const cancel = (e:Event) => {
			if (longpressed) e.preventDefault();
			clearTimeout(timeout);
			node.removeEventListener('mousemove', cancel);
			node.removeEventListener('mouseup', cancel);
			node.removeEventListener('touchmove', cancel);
			node.removeEventListener('touchend', cancel);
		};
		
		node.addEventListener('mousemove', cancel);
		node.addEventListener('mouseup', cancel);
		node.addEventListener('touchmove', cancel);
		node.addEventListener('touchend', cancel);
	}
	
	node.addEventListener('mousedown', start);
	node.addEventListener('touchstart', start);

	
	return {
		destroy() {
			node.removeEventListener('mousedown', start);
			node.removeEventListener('touchstart', start);
		}
	};
}

export function phoneNumberFormat (v: string, pattern: string) {
	let i = 0;
	return pattern.replace(/#/g, () => v[i++]);
}